import { ref, computed, watchEffect } from 'vue';
import { useAlert } from 'dashboard/composables';
import wootAPI from '@/v3/api/apiClient';

/**
 * Composable for handling conversation agent logic
 * @param {Object} inboxId - The inbox ID
 * @param {Object} currentChat - The current chat object
 * @returns {Object} Conversation agent utilities
 */
export function useConversationAgent(inboxId, currentChat) {
  const agentName = ref('Nenhum');
  const hasIaAgent = ref(false);
  const iaAgentName = ref(null);
  const loading = ref(false);

  /**
   * Check if conversation has 'atendimento-humano' label
   */
  const hasAtendimentoLabel = computed(() => {
    return currentChat.value?.labels?.includes('atendimento-humano') || false;
  });

  /**
   * Get cookie value by name
   */
  function getCookie(name) {
    const match = document.cookie.match(
      new RegExp('(^| )' + name + '=([^;]+)')
    );
    return match ? decodeURIComponent(match[2]) : null;
  }

  /**
   * Get authentication headers from session cookie
   */
  function getAuthHeaders() {
    const cookie = getCookie('cw_d_session_info');
    if (!cookie) return {};
    try {
      const sessionInfo = JSON.parse(cookie);
      return {
        'access-token': sessionInfo['access-token'],
        client: sessionInfo.client,
        uid: sessionInfo.uid,
      };
    } catch (e) {
      console.error('Error parsing cookie:', e);
      return {};
    }
  }

  /**
   * Fetch agent information from API
   */
  async function fetchAgentName() {
    if (!inboxId.value) return;

    try {
      const headers = getAuthHeaders();
      const response = await wootAPI.get('/api/v1/inboxmeta/fetch_agentis', {
        params: { inbox_id: inboxId.value },
        headers,
      });

      const hasAgent = response.data && response.data.agent;
      hasIaAgent.value = hasAgent;
      iaAgentName.value = response.data?.agent || null;

      updateAgentName();
    } catch (error) {
      console.error('Error fetching agent name:', error);
      agentName.value = 'Nenhum agente IA';
      hasIaAgent.value = false;
      iaAgentName.value = null;
    }
  }

  /**
   * Update agent name based on current state
   */
  function updateAgentName() {
    if (!hasIaAgent.value) {
      agentName.value = 'Nenhum agente IA';
    } else if (hasAtendimentoLabel.value) {
      agentName.value = 'Conversa assumida por humano';
    } else {
      agentName.value = `Atendido por ${iaAgentName.value || 'IA'} (IA)`;
    }
  }

  /**
   * Handle conversation assumption/resumption
   */
  async function handleAssumeConversation(route, store) {
    loading.value = true;

    try {
      const conversationId = currentChat.value?.id;
      const accountId = route.params.accountId;

      if (!conversationId || !accountId) {
        throw new Error('Missing conversation ID or account ID');
      }

      // Call the toggle endpoint
      const response = await wootAPI.post(
        `/api/v1/accounts/${accountId}/conversations/${conversationId}/labels/toggle`,
        {},
        {
          headers: getAuthHeaders(),
        }
      );

      // Update the conversation labels in the store
      if (response.data && response.data.labels) {
        store.dispatch('conversationLabels/setConversationLabel', {
          id: conversationId,
          data: response.data.labels,
        });

        // Also update the current conversation object
        store.commit('UPDATE_CONVERSATION', {
          ...currentChat.value,
          labels: response.data.labels,
        });
      }

      // Show appropriate success message
      if (hasAtendimentoLabel.value) {
        useAlert('Atendimento IA retomado com sucesso!');
      } else {
        useAlert('Conversa assumida com sucesso!');
      }
    } catch (error) {
      console.error('Error toggling conversation label:', error);
      if (hasAtendimentoLabel.value) {
        useAlert('Erro ao retomar atendimento IA', 'error');
      } else {
        useAlert('Erro ao assumir conversa', 'error');
      }
    } finally {
      loading.value = false;
    }
  }

  // Watch for changes and fetch agent name automatically
  watchEffect(() => {
    if (inboxId.value || currentChat.value?.id) {
      fetchAgentName();
    }
  });

  // Watch for hasAtendimentoLabel changes
  watchEffect(() => {
    if (hasAtendimentoLabel.value !== undefined) {
      updateAgentName();
    }
  });

  return {
    agentName,
    hasIaAgent,
    iaAgentName,
    loading,
    hasAtendimentoLabel,
    fetchAgentName,
    updateAgentName,
    handleAssumeConversation,
  };
}